import { maxBy } from 'lodash';
import { Edge, TestResults } from './types';
import { AUDIO_LEVEL_THRESHOLD } from './constants';
import { DNS_Timeout, MEDIA_SERVER_ERROR_CODE } from './NetworkTestWidget/Tests/Tests.constants';
import { CheckConnectivitiyResultsConstants } from './Core/constants/AppConstants';

export function getAudioLevelPercentage(level: number) {
  return (level * 100) / AUDIO_LEVEL_THRESHOLD; // 0 to 100
}

export function getStandardDeviation(values: number[]): number {
  // Same method used in client sdks
  // https://github.com/twilio/twilio-client.js/blob/master/lib/twilio/statsMonitor.ts#L88

  if (values.length <= 0) {
    return 0;
  }

  const valueAverage: number =
    values.reduce((partialSum: number, value: number) => partialSum + value, 0) / values.length;

  const diffSquared: number[] = values.map((value: number) => Math.pow(value - valueAverage, 2));

  const stdDev: number = Math.sqrt(
    diffSquared.reduce((partialSum: number, value: number) => partialSum + value, 0) / diffSquared.length
  );

  return round(stdDev);
}

export function getJSON(url: string) {
  return fetch(url).then(async (res) => {
    if (res.status === 401) {
      throw new Error('expired');
    }

    if (!res.ok) {
      throw new Error(res.statusText);
    }

    return await res.json();
  });
}

export const getBestEdge = (results: TestResults[]) =>
  maxBy(results, (result) => result.results.preflight?.stats?.mos?.average);

export const round = (num: number, decimals = 2) =>
  Math.round((num + Number.EPSILON) * 10 ** decimals) / 10 ** decimals;

export function regionalizeIceUrls(edge: Edge, iceServers: RTCIceServer[]) {
  if (edge === 'roaming') {
    return iceServers;
  }

  return iceServers.map((server: RTCIceServer) => {
    const result = {
      ...server,
    };

    if (result.url) {
      result.url = result.url.replace('global', edge);
    }

    if (typeof result.urls === 'string') {
      result.urls = result.urls.replace('global', edge);
    }

    if (Array.isArray(result.urls)) {
      result.urls = result.urls.map((url) => url.replace('global', edge));
    }
    return result;
  });
}

export const codecNameMap = {
  opus: 'Opus',
  pcmu: 'PCMU',
};

export const edgeNameMap = {
  sydney: 'Sydney',
  'sao-paulo': 'Sao Paulo',
  dublin: 'Dublin',
  frankfurt: 'Frankfurt',
  tokyo: 'Tokyo',
  singapore: 'Singapore',
  ashburn: 'Ashburn',
  roaming: 'Roaming',
  'ashburn-ix': 'Ashburn IX',
  'san-jose-ix': 'San Jose IX',
  'london-ix': 'London IX',
  'frankfurt-ix': 'Frankfurt IX',
  'singapore-ix': 'Singapore IX',
};

export function getEdgeName(result: TestResults) {
  if (result.results.preflight?.selectedEdge === 'roaming') {
    return `Roaming - ${edgeNameMap[result.results.preflight?.edge as Edge]}`;
  }

  return edgeNameMap[result.edge as Edge];
}

/**
 * Makes a no-cors mode request to check if the Url is reachable.
 */
export async function makeNoCorsFetchrequest(url: string) {
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), DNS_Timeout);
    const response = await fetch(url, {
      mode: 'no-cors',
      signal: controller.signal,
    });
    clearTimeout(id);
    return response;
  } catch (err) {
    console.log('Error making reqeust', err);
    return Promise.reject(err);
  }
}

export function getDocumentationLink(results: TestResults): any {
  const { results: connectivitiyResults, errors: connectivityErrors } = results;

  const {
    documentationLinks: {
      signallingErrorDocumentationLink,
      mediaServerErrorDocumentationLink,
      badCallQualityDocumentationLink,
    },
  } = CheckConnectivitiyResultsConstants;

  return (!connectivitiyResults?.preflight && connectivityErrors.preflight?.code !== MEDIA_SERVER_ERROR_CODE)
    ? [signallingErrorDocumentationLink, mediaServerErrorDocumentationLink]
    : connectivityErrors.preflight?.code === 31003
    ? mediaServerErrorDocumentationLink
    : badCallQualityDocumentationLink;
}

export function getConnectivityAlertVariant(results: TestResults) {
  const { results: connectivitiyResults, errors: connectivityErrors } = results;

  const mosValue = connectivitiyResults.preflight?.stats?.mos?.average;

  return !connectivitiyResults?.preflight || connectivityErrors.preflight?.code === 31003
    ? 'error'
    : mosValue && mosValue < 4.1
    ? 'warning'
    : 'success';
}

export function getDNSlookupAlertVariant(results: TestResults) {
  const { results: connectivitiyResults } = results;

  return connectivitiyResults.dnsLookupTest?.totalDomains !== connectivitiyResults.dnsLookupTest?.domainsReachable
    ? 'error'
    : 'success';
}

export function getQueryParams(queryString: string) {
  let queryParamsArray = queryString.substring(1).split('&');

  const allQueryParams: any = {};

  queryParamsArray.forEach((queryParam) => {
    const currentQueryParam = queryParam.split('=');

    allQueryParams[currentQueryParam[0]] = currentQueryParam[1];
  });

  return allQueryParams;
}
